import { Button, Flex } from "antd";
import { CircleDashedIcon } from "@phosphor-icons/react";
import { useTranslate } from "@refinedev/core";
import { useAntTheme } from "hooks/useAntTheme";
import { DEFAULT_STROKE_COLOR } from "../../constants";
import { toCircularMask } from "../../helpers/maskHelper";
import { ItemAlignmentFeature } from "../features/ItemAlignmentFeature";
import { useTemplateEditorStore } from "../../store/useTemplateEditorStore";
import { useTemplatesStatusStore } from "../../store/useTemplatesStatusStore";
import { MediaItem } from "../../types";
import { BorderRadius } from "../controls/BorderRadius";
import { Stroke } from "../controls/Stroke";
import { StrokeColorControl } from "../controls/StrokeColorControl";
import { ToolbarDivider } from "./ToolbarDivider";
import { PanelSection } from "../StoryboardMenuTabs/PanelSection";
import { useTemplateData } from "../../hooks/useTemplateData";
import { useMemo } from "react";
import { ItemControlsConfigProvider } from "../controls/ItemControlsConfigProvider";
import { getRelativeDimentionsToCalculateRadius } from "../../helpers";

export const ImageVideoControls = ({
  previewWidth,
  previewHeight,
}: {
  previewWidth: number;
  previewHeight: number;
}) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const {
    brandKitData: { brandKitPalette: palette },
  } = useTemplateData();
  const {
    currentItem,
    mainImage,
    images,
    videos,
    updateMainImage,
    updateImage,
    updateVideo,
  } = useTemplateEditorStore((state) => ({
    currentItem: state.templateState.currentItem,
    mainImage: state.templateState.mainImage,
    images: state.templateState.images,
    videos: state.templateState.videos,
    updateMainImage: state.updateMainImage,
    updateImage: state.updateImage,
    updateVideo: state.updateVideo,
  }));

  const { addTemplateUpdate } = useTemplatesStatusStore((state) => ({
    addTemplateUpdate: state.addTemplateUpdate,
  }));

  const currentMainImage =
    mainImage?.id === currentItem?.id ? mainImage : undefined;
  const currentImage = images.find((image) => image.id === currentItem?.id);
  const currentVideo = videos.find((video) => video.id === currentItem?.id);

  const pickedItem = currentMainImage ?? currentImage ?? currentVideo;

  const pickedItemAction = useMemo(() => {
    if (currentMainImage) {
      return updateMainImage;
    }
    if (currentImage) {
      return updateImage;
    }
    return updateVideo;
  }, [
    updateMainImage,
    updateImage,
    updateVideo,
    currentMainImage,
    currentImage,
  ]);

  if (!pickedItem) return null;

  const strokeControl = (
    <Stroke
      value={pickedItem.stroke?.width ?? 0}
      onChange={(value) => {
        pickedItemAction({
          ...pickedItem,
          stroke: value
            ? {
                width: value / previewWidth,
                color: pickedItem.stroke?.color ?? DEFAULT_STROKE_COLOR,
              }
            : undefined,
        });
        addTemplateUpdate();
      }}
    />
  );

  const strokeColorControl = (
    <StrokeColorControl
      item={pickedItem}
      pickedItemAction={pickedItemAction}
      previewWidth={previewWidth}
      palette={palette}
    />
  );

  const borderRadiusControl = (
    <BorderRadius
      value={pickedItem.stroke?.radius ?? 0}
      maxValue={Math.round((pickedItem.size.width / 2) * previewWidth)}
      onChange={(value) => {
        pickedItemAction({
          ...pickedItem,
          stroke: { ...pickedItem.stroke, radius: value },
        });
        addTemplateUpdate();
      }}
      relativeDimentions={getRelativeDimentionsToCalculateRadius(
        previewWidth,
        previewHeight
      )}
    />
  );

  const isCircularMask = pickedItem.mask?.shape === "ellipse";
  const circularMaskControl = (
    <Button
      type="text"
      size="middle"
      aria-pressed={isCircularMask}
      icon={<CircleDashedIcon weight="bold" size={18} />}
      style={{
        backgroundColor: isCircularMask ? theme.colorLightGrey : undefined,
      }}
      onClick={() => {
        pickedItemAction({
          ...pickedItem,
          mask: isCircularMask
            ? undefined
            : toCircularMask(
                pickedItem.mask,
                pickedItem.size.width * previewWidth,
                pickedItem.size.height * previewHeight
              ),
        });
        addTemplateUpdate();
      }}
    >
      {t("components.templates.SettingsToolbar.controls.circularMask")}
    </Button>
  );

  const alignmentControl = (popover: boolean) => (
    <ItemAlignmentFeature
      pickedItem={pickedItem}
      onChange={(value) => {
        pickedItemAction(value as Partial<MediaItem>);
        addTemplateUpdate();
      }}
      previewWidth={previewWidth}
      previewHeight={previewHeight}
      popover={popover}
    />
  );

  return (
    <ItemControlsConfigProvider>
      <PanelSection
        title={t("components.templates.settings.mediaSettings", {
          type: currentItem?.type,
        })}
      >
        <Flex gap={16} align="start">
          {alignmentControl(false)}
          <ToolbarDivider style={{ alignSelf: "stretch", height: "auto" }} />
          <Flex vertical gap={12} align="start">
            <Flex align="center" gap={8}>
              {strokeControl}
              {strokeColorControl}
              {borderRadiusControl}
            </Flex>
            {circularMaskControl}
          </Flex>
        </Flex>
      </PanelSection>
    </ItemControlsConfigProvider>
  );
};
